import type {ProjectPickerRelayProject as ProjectPickerProject} from '@github-ui/item-picker-project-relay'
import {ProjectSymlinkIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {BulkProjectPicker} from '../bulk-pickers/BulkProjectPicker'
import type {SharedListHeaderActionProps} from '../types'
import type {AddToProjectsBulkActionQuery} from './__generated__/AddToProjectsBulkActionQuery.graphql'

type AddToProjectsActionProps = {
  issueIds: string[]
} & SharedListHeaderActionProps

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const AddToProjectsQuery = graphql`
  query AddToProjectsBulkActionQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Issue {
        id
        projectItemsNext(first: 10) {
          edges {
            node {
              id
              project {
                # eslint-disable-next-line relay/unused-fields
                id
                # eslint-disable-next-line relay/unused-fields
                title
                # eslint-disable-next-line relay/unused-fields
                closed
                # eslint-disable-next-line relay/unused-fields
                number
                # eslint-disable-next-line relay/unused-fields
                url
                # eslint-disable-next-line relay/unused-fields
                viewerCanUpdate
                # eslint-disable-next-line relay/unused-fields
                hasReachedItemsLimit
                # eslint-disable-next-line relay/unused-fields
                __typename
              }
            }
          }
        }
      }
    }
  }
`
export const AddToProjectsBulkAction = ({disabled = false, nested, ...props}: AddToProjectsActionProps) => {
  const [wasTriggered, setWasTriggered] = useState(false)

  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      const anchorText = 'Project'

      if (nested) {
        return (
          <ActionList.Item disabled={disabled} {...anchorProps} role="menuitem">
            <ActionList.LeadingVisual>
              <ProjectSymlinkIcon />
            </ActionList.LeadingVisual>
            {anchorText}
          </ActionList.Item>
        )
      }

      return (
        <Button
          data-testid={'bulk-add-to-project-button'}
          disabled={disabled}
          leadingVisual={ProjectSymlinkIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
        >
          {anchorText}
        </Button>
      )
    },
    [disabled, nested],
  )

  if (!wasTriggered) {
    return anchorElement({
      onClick: () => {
        setWasTriggered(true)
      },
    })
  }

  return <LazyBulkProjectPicker disabled={disabled} anchorElement={anchorElement} {...props} />
}

const LazyBulkProjectPicker = ({
  issuesToActOn,
  anchorElement,
  onCompleted,
  onError,
  nested,
  owner,
  repo,
}: AddToProjectsActionProps & {anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement}) => {
  //get selected issues IDs
  const {nodes: updatedIssueNodes} = useLazyLoadQuery<AddToProjectsBulkActionQuery>(
    AddToProjectsQuery,
    {ids: issuesToActOn},
    {fetchPolicy: 'store-or-network'},
  )

  const existingIssueProjects = new Map<string, string[]>()
  const selectedIssuesProjects = (updatedIssueNodes || []).map(node => {
    const nodeId = node?.id
    if (!nodeId) return []

    const projects = (node.projectItemsNext?.edges || []).flatMap(a => (a?.node?.project ? [a.node.project] : []))

    return (
      projects.map(project => {
        const existingProjects = existingIssueProjects.get(nodeId) || []
        existingProjects.push(project.id)
        existingIssueProjects.set(nodeId, existingProjects)

        return {issueId: nodeId, projectId: project.id, projectNode: project}
      }) ?? []
    )
  })

  const projectAppliedToAll = {} as {[key: string]: ProjectPickerProject}
  for (const issueProjects of selectedIssuesProjects) {
    for (const project of issueProjects) {
      if (project.projectId in projectAppliedToAll) continue

      if (
        selectedIssuesProjects.every(otherIssueProjects =>
          otherIssueProjects.some(p => p.projectId === project.projectId),
        )
      ) {
        projectAppliedToAll[project.projectId] = project.projectNode
      }
    }
  }

  return (
    <BulkProjectPicker
      pickerId={'add-to-projects-project-picker'}
      issueIds={issuesToActOn}
      readonly={false}
      triggerOpen
      selectedProjects={Object.values(projectAppliedToAll)}
      onCompleted={onCompleted}
      onError={onError}
      nested={nested}
      anchorElement={anchorElement}
      owner={owner}
      repo={repo}
    />
  )
}

try{ AddToProjectsBulkAction.displayName ||= 'AddToProjectsBulkAction' } catch {}
try{ LazyBulkProjectPicker.displayName ||= 'LazyBulkProjectPicker' } catch {}